import { useState, useEffect } from 'react';
import { Calendar, AlertTriangle } from 'lucide-react';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Alert from '../ui/Alert';
import { useAuth } from '../../contexts/AuthContext';
import ceuService from '../../services/unifiedCEUService';
import userService from '../../services/unifiedUserService';
import dayjs from 'dayjs';

export default function CycleExtensionManager() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [cycles, setCycles] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [extensionUsed, setExtensionUsed] = useState(false);

  console.log('CycleExtensionManager rendered, user:', user);

  useEffect(() => {
    if (user?.userId) {
      loadCycles();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.userId]);

  const loadCycles = async () => {
    if (!user?.userId) {
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      console.log('Loading cycles for user:', user.userId);
      
      const [ceusResult, profileResult] = await Promise.all([
        ceuService.getCEUs(user.userId),
        userService.getUser(user.userId)
      ]);

      console.log('CEUs result:', ceusResult);
      console.log('Profile result:', profileResult);

      // CEU service returns {success, data} object
      const ceus = ceusResult.success ? ceusResult.data : (Array.isArray(ceusResult) ? ceusResult : []);
      
      console.log('Extracted CEUs:', ceus.length, 'items');

      if (!profileResult || !profileResult.success) {
        const errorMsg = profileResult?.error || 'Profile not found';
        console.error('Profile load failed:', errorMsg);
        
        // If profile doesn't exist, that's okay - user just needs to complete their profile
        if (errorMsg.includes('not found')) {
          console.log('Profile not found - user may need to complete profile setup');
          // Continue with empty profile data
        } else {
          setError('Failed to load profile: ' + errorMsg);
          setLoading(false);
          return;
        }
      }

      // Group CEUs by cycle with better deduplication
      const cycleMap = {};
      ceus.forEach(ceu => {
        console.log('Processing CEU:', ceu.title, 'cycleStartDate:', ceu.cycleStartDate, 'cycleEndDate:', ceu.cycleEndDate);
        if (ceu.cycleStartDate && ceu.cycleEndDate) {
          // Use start date as primary key to avoid duplicates from extensions
          const key = ceu.cycleStartDate;
          if (!cycleMap[key]) {
            cycleMap[key] = {
              startDate: ceu.cycleStartDate,
              endDate: ceu.cycleEndDate,
              ceus: [],
              isCurrent: ceu.isCurrentCycle
            };
          } else {
            // If we already have this cycle, use the latest end date (in case of extensions)
            if (dayjs(ceu.cycleEndDate).isAfter(dayjs(cycleMap[key].endDate))) {
              cycleMap[key].endDate = ceu.cycleEndDate;
            }
          }
          cycleMap[key].ceus.push(ceu);
        }
      });

      console.log('CycleMap after CEUs:', cycleMap);

      // Add current cycle from profile only if it doesn't already exist
      const profile = profileResult?.data || profileResult?.user || {};
      console.log('Profile cycle dates:', profile?.cycleStartDate, profile?.cycleEndDate);
      
      // Check if user has already used their extension
      setExtensionUsed(profile?.extensionUsed || false);
      
      if (profile?.currentCycleStart && profile?.currentCycleEnd) {
        const currentKey = profile.currentCycleStart;
        if (!cycleMap[currentKey]) {
          cycleMap[currentKey] = {
            startDate: profile.currentCycleStart,
            endDate: profile.currentCycleEnd,
            ceus: [],
            isCurrent: true
          };
        }
      }

      const cycleList = Object.values(cycleMap).sort((a, b) => 
        dayjs(b.startDate).unix() - dayjs(a.startDate).unix()
      );

      console.log('Cycles loaded:', cycleList);
      setCycles(cycleList);
      setLoading(false);
    } catch (err) {
      console.error('Error in loadCycles:', err);
      setError('Error loading cycles: ' + err.message);
      setLoading(false);
    }
  };

  const handleExtendCycle = async (cycle, newEndDate) => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      // Get ALL CEUs for this user to ensure we update everything
      const allCEUsResult = await ceuService.getCEUs(user.userId);
      if (!allCEUsResult.success) {
        setError('Failed to load CEUs for update');
        setLoading(false);
        return;
      }

      const allCEUs = allCEUsResult.data || [];
      
      // Find all CEUs that match this cycle's START date (more reliable than start+end)
      const ceusToUpdate = allCEUs.filter(ceu => 
        ceu.cycleStartDate === cycle.startDate
      );

      console.log(`Extending cycle ${cycle.startDate} to ${cycle.endDate} -> ${newEndDate}`);
      console.log(`Updating ${ceusToUpdate.length} CEUs`);

      // Update cycleEndDate for all CEUs in this cycle
      const updatePromises = ceusToUpdate.map(ceu =>
        ceuService.updateCEU(user.userId, ceu.ceuId, {
          cycleEndDate: newEndDate
        })
      );

      await Promise.all(updatePromises);

      // Update user profile with extension info
      const profileResult = await userService.getUser(user.userId);
      if (profileResult.success) {
        const updatedProfile = {
          ...profileResult.data,
          extensionUsed: true,
          extensionGrantedAt: new Date().toISOString(),
          // Update the appropriate cycle end date
          ...(cycle.isCurrent ? {
            currentCycleEnd: newEndDate
          } : {
            // For previous cycles, we might need to track this differently
            cycleEndDate: newEndDate
          }),
          updatedAt: new Date().toISOString()
        };
        
        await userService.updateUser(user.userId, updatedProfile);
        setExtensionUsed(true);
      }

      setSuccess(`Cycle extended to ${dayjs(newEndDate).format('MMM D, YYYY')}. ${ceusToUpdate.length} CEUs updated.`);
      await loadCycles();
    } catch (err) {
      console.error('Extension error:', err);
      setError('Failed to extend cycle: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading && cycles.length === 0) {
    return (
      <div 
        className="text-center py-8 text-gray-500" 
        role="status" 
        aria-live="polite"
        aria-label="Loading cycle information"
      >
        Loading cycles...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Calendar className="w-5 h-5 text-primary" aria-hidden="true" />
        <h2 className="text-lg font-semibold text-gray-900">Manage Cycle Extensions</h2>
      </div>

      <div className="border border-primary/20 bg-primary/5 rounded-lg p-4">
        <p className="text-sm text-gray-700">
          Extend a cycle's end date to ensure your CEUs are grouped correctly. This is useful if you received an extension from RID/BEI.
        </p>
      </div>

      {error && <Alert variant="error">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      <div className="space-y-4">
        {cycles.map((cycle, index) => (
          <CycleCard
            key={index}
            cycle={cycle}
            onExtend={handleExtendCycle}
            disabled={loading}
            extensionUsed={extensionUsed}
          />
        ))}

        {cycles.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No cycles found. Add some CEUs to get started.
          </div>
        )}
      </div>

      <div className="border border-error/20 bg-error/5 rounded-lg p-4">
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-5 h-5 text-error flex-shrink-0 mt-0.5" aria-hidden="true" />
          <div>
            <h3 className="font-semibold text-error mb-1">RID Extension Policy</h3>
            <p className="text-sm text-gray-700">
              Extensions are only allowed <strong>once in your lifetime</strong> per certification.{' '}
              <a 
                href="https://rid.org/cycle-extension-form/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary hover:underline"
                aria-label="Learn more about RID extension policy (opens in new tab)"
              >
                Learn more →
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function CycleCard({ cycle, onExtend, disabled, extensionUsed }) {
  const [isEditing, setIsEditing] = useState(false);
  const [newEndDate, setNewEndDate] = useState(cycle.endDate);

  const totalCEUs = cycle.ceus.reduce((sum, ceu) => sum + (ceu.ceuHours || 0), 0);

  const handleSave = () => {
    onExtend(cycle, newEndDate);
    setIsEditing(false);
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-gray-900">
              {dayjs(cycle.startDate).format('MMM YYYY')} - {dayjs(cycle.endDate).format('MMM YYYY')}
            </h3>
            {cycle.isCurrent && (
              <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs font-medium rounded">
                Current
              </span>
            )}
          </div>
          <p className="text-sm text-gray-600 mt-1">
            {cycle.ceus.length} workshops • {totalCEUs.toFixed(1)} CEUs
          </p>
        </div>
      </div>

      {isEditing ? (
        <div className="space-y-3 pt-3 border-t border-gray-200">
          <Input
            type="date"
            label="New End Date"
            value={newEndDate}
            onChange={(e) => setNewEndDate(e.target.value)}
            disabled={disabled}
          />
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSave} disabled={disabled}>
              Save Extension
            </Button>
            <Button size="sm" variant="outline" onClick={() => setIsEditing(false)} disabled={disabled}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        !extensionUsed && (
          <Button size="sm" variant="outline" onClick={() => setIsEditing(true)} disabled={disabled}>
            Extend Cycle
          </Button>
        )
      )}
      
      {extensionUsed && !isEditing && (
        <p className="text-sm text-gray-500 italic">Extension already used (one-time limit)</p>
      )}
    </div>
  );
}
